import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import PlanAwaitingPaymentBanner from "./PlanAwaitingPaymentBanner";
import GuestMembershipBanner from "./GuestMembershipBanner";
import { useSidebarToggle } from "../hooks/useSidebarToggle";
import { setShellSearch } from "../shared/shellSearch";
// Every page inside the frame: one typeface, calmer sizes, phone-friendly tables, tabs, cards and modals
import "../styles/feedPages.css";

// The app's frame from the 2026-10 dashboard design (feedShell.css): the white top bar fixed at
// the top, the light-green sidebar beside the page, and the page itself. Shared by Home.jsx
// (/home, /services, /home/profile…) and ServicesLayout.jsx (every service's pages) so both
// always look and behave the same.
//
// Sidebar: at 992px and wider it sits beside the page and the top bar's ☰ narrows it to icons
// (remembered, useSidebarToggle); narrower, it is a drawer that ☰ slides in over the page.
//
// `renderSidebar({ collapsed, onToggleSidebar, isSidebarExpanded })` draws the SideNav; leave it
// out (or showSidebar=false) for pages without one. The main area keeps the theme's `user-area`
// class: page tables and cards are styled through `.user-area …` rules in style.css.
export default function AppShell({ showSidebar = true, renderSidebar, children, before }) {
  const {
    isMobileSidebarOpen,
    setIsMobileSidebarOpen,
    isDesktopCollapsed,
    toggle,
    isExpanded,
  } = useSidebarToggle();
  const withSidebar = showSidebar && !!renderSidebar;

  // The top bar's search is for the page in front of you: a new page starts unfiltered
  const { pathname } = useLocation();
  useEffect(() => { setShellSearch(""); }, [pathname]);

  const classes = ["fw-app"];
  if (!withSidebar) classes.push("fw-no-sidebar");
  if (withSidebar && isDesktopCollapsed) classes.push("fw-collapsed");
  if (withSidebar && isMobileSidebarOpen) classes.push("fw-drawer-open");

  return (
    <div className={classes.join(" ")}>
      {before}
      <Header showSidebarToggle={withSidebar} isSidebarExpanded={isExpanded} onToggleSidebar={toggle} />
      {withSidebar && (
        <div
          className="fw-backdrop"
          aria-hidden="true"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}
      <div className="fw-layout">
        {withSidebar && (
          <aside className="fw-sidebar" aria-label="Main menu">
            {renderSidebar({ collapsed: isDesktopCollapsed, onToggleSidebar: toggle, isSidebarExpanded: isExpanded })}
          </aside>
        )}
        <main className="fw-main user-area">
          {/* A paid plan approved with the FPO / Company registration, waiting for the online payment: on every page */}
          <PlanAwaitingPaymentBanner />
          {/* Registered without paying the membership fee: guest access, and the way to pay it */}
          <GuestMembershipBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
